import { useEffect, useState } from "react";
import { API_BASE } from "../lib/apiBase";

interface Props {
  /** [lng, lat] of the point to look at. */
  coord: [number, number];
  /** Optional heading override (degrees, 0 = north). */
  heading?: number;
}

type Status = "loading" | "available" | "none";

/**
 * Ground-truth street-level photo for a clicked location, served through
 * /api/streetview/meta + /api/streetview.
 *
 * Backed by KartaView (open, keyless, CC BY-SA) since Google Street View was
 * billed per image. The practical difference for the viewer: these are real
 * captured frames along roads rather than a synthesised panorama, so `heading`
 * no longer steers the view and interior campus points may have no photo at
 * all — hence the explicit "no imagery" state below.
 */
export function StreetViewThumb({ coord, heading = 0 }: Props) {
  const [lng, lat] = coord;
  const [status, setStatus] = useState<Status>("loading");
  const [date, setDate] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    fetch(`${API_BASE}/api/streetview/meta?lat=${lat}&lng=${lng}`)
      .then((r) => r.json())
      .then((d: { features?: Array<{ available?: boolean; date?: string | null }> }) => {
        if (!alive) return;
        const f = d.features?.[0];
        setStatus(f?.available ? "available" : "none");
        setDate(f?.date ?? null);
      })
      .catch(() => alive && setStatus("none"));
    return () => {
      alive = false;
    };
  }, [lat, lng]);

  if (status === "loading") {
    return (
      <div className="streetview-thumb streetview-thumb--msg mono">
        <span className="dot loading" /> Street level…
      </div>
    );
  }
  if (status === "none") {
    return <div className="streetview-thumb streetview-thumb--msg mono">No street-level photo here</div>;
  }

  const src = `${API_BASE}/api/streetview?lat=${lat}&lng=${lng}&heading=${heading}&size=320x180`;
  return (
    <div className="streetview-thumb">
      <img src={src} alt="Street-level photo of this location" loading="lazy" />
      <span className="streetview-cred mono">
        STREET LEVEL · KARTAVIEW{date ? ` · ${date}` : ""}
      </span>
    </div>
  );
}
